/**
 * Deterministic Return Generator Service (GSTR-1, GSTR-3B, GSTR-9)
 * Replicated & adapted from resilient-tech/india-compliance (gst_returns & gstr3b utils)
 * apps/gst-portal/src/services/returnGeneratorService.ts
 */

import Decimal from 'decimal.js';
import {
  ExtractedGstInvoice,
  Gstr2bRecord,
  Gstr1Summary,
  Gstr3bSummary,
  Gstr9AnnualSummary,
  Gstr1OfficialPayload,
  Gstr1B2bGroup,
  Gstr1B2clInvoice,
  Gstr1B2csItem,
  Gstr1HsnItem,
  Gstr1DocIssueSection,
  Gstr3bOfficialPayload
} from '../types/gst';
import { runGstr2bReconciliation } from './reconciliationService';

/**
 * Converts tax period and financial year to GSTN period code (e.g. "April" + "2025-26" -> "042025")
 */
export function formatGstnReturnPeriod(taxPeriod: string, financialYear: string): string {
  const monthMap: Record<string, string> = {
    "April": "04", "May": "05", "June": "06",
    "July": "07", "August": "08", "September": "09",
    "October": "10", "November": "11", "December": "12",
    "January": "01", "February": "02", "March": "03"
  };

  const monthNum = monthMap[taxPeriod] || "04";
  const years = financialYear.split("-");
  const startYear = parseInt(years[0]) || 2025;
  const endYear = (startYear + 1).toString();

  // If Jan-Mar, year is end year; otherwise start year
  const actualYear = ["01", "02", "03"].includes(monthNum) ? endYear : startYear.toString();
  return `${monthNum}${actualYear}`;
}

/**
 * Generates GSTR-1 Summary and Official GSTN JSON
 * Adapting India Compliance sections: b2b, b2cl, b2cs, exp, hsn, doc_issue
 */
export function generateGstr1(
  gstin: string,
  financialYear: string,
  taxPeriod: string,
  salesInvoices: ExtractedGstInvoice[]
): Gstr1Summary {
  const fp = formatGstnReturnPeriod(taxPeriod, financialYear);
  const companyState = gstin.substring(0, 2);

  // Filter Sales invoices
  const sales = salesInvoices.filter(
    inv => inv.category === 'B2B_SALES' || inv.category === 'B2C_RETAIL'
  );

  let b2bCount = 0;
  let b2bTaxable = new Decimal(0);
  let b2bTax = new Decimal(0);

  let b2cCount = 0;
  let b2cTaxable = new Decimal(0);
  let b2cTax = new Decimal(0);

  let exportCount = 0;
  let exportTaxable = new Decimal(0);
  let exportTax = new Decimal(0);

  // Buckets for GSTN JSON
  const b2bMap: Record<string, any[]> = {};
  const b2clList: Gstr1B2clInvoice[] = [];
  const b2csMap: Record<string, {
    sply_ty: 'INTER' | 'INTRA';
    pos: string;
    rt: number;
    txval: Decimal;
    iamt: Decimal;
    camt: Decimal;
    samt: Decimal;
    csamt: Decimal;
  }> = {};
  const expInvoices: any[] = [];
  const hsnMap: Record<string, { desc: string; qty: Decimal; val: Decimal; taxable: Decimal; iamt: Decimal; camt: Decimal; samt: Decimal; csamt: Decimal }> = {};
  const docNumbers: string[] = [];

  for (const inv of sales) {
    const invTaxable = new Decimal(inv.total_taxable_value || 0);
    const invTaxVal = new Decimal(inv.total_tax || 0);
    const buyerGstin = (inv.buyer_gstin || "").trim().toUpperCase();
    const isRegistered = buyerGstin.length === 15 && buyerGstin !== "URP";
    const pos = (inv.place_of_supply || (isRegistered ? buyerGstin.substring(0, 2) : companyState)).substring(0, 2);
    const isInterState = pos !== companyState;
    const isExport = (inv.place_of_supply || "").toLowerCase().includes("export") || buyerGstin === "EXPORT";

    if (inv.invoice_number) {
      docNumbers.push(inv.invoice_number);
    }

    if (isExport) {
      exportCount++;
      exportTaxable = exportTaxable.plus(invTaxable);
      exportTax = exportTax.plus(invTaxVal);

      expInvoices.push({
        inum: inv.invoice_number,
        idt: inv.invoice_date,
        val: inv.invoice_total,
        itms: (inv.line_items || []).map((l, idx) => ({
          num: idx + 1,
          itm_det: {
            txval: l.taxable_value,
            rt: (l.igst_rate || 0) + (l.cgst_rate || 0) + (l.sgst_rate || 0),
            iamt: l.igst_amount || 0,
            camt: 0,
            samt: 0,
            csamt: l.cess_amount || 0
          }
        }))
      });
    } else if (isRegistered) {
      // 1. Table 4: B2B Regular
      b2bCount++;
      b2bTaxable = b2bTaxable.plus(invTaxable);
      b2bTax = b2bTax.plus(invTaxVal);

      if (!b2bMap[buyerGstin]) b2bMap[buyerGstin] = [];
      b2bMap[buyerGstin].push({
        inum: inv.invoice_number,
        idt: inv.invoice_date,
        val: inv.invoice_total,
        pos,
        rchrg: inv.reverse_charge_applicable ? "Y" : "N",
        inv_typ: "R",
        itms: (inv.line_items || []).map((l, idx) => ({
          num: idx + 1,
          itm_det: {
            txval: l.taxable_value,
            rt: (l.cgst_rate || 0) + (l.sgst_rate || 0) + (l.igst_rate || 0),
            iamt: l.igst_amount || 0,
            camt: l.cgst_amount || 0,
            samt: l.sgst_amount || 0,
            csamt: l.cess_amount || 0
          }
        }))
      });
    } else {
      // Unregistered Buyer (B2C)
      b2cCount++;
      b2cTaxable = b2cTaxable.plus(invTaxable);
      b2cTax = b2cTax.plus(invTaxVal);

      // India Compliance / GST Rule: If Inter-State AND invoice_total > ₹2,50,000 -> B2C Large (b2cl)
      if (isInterState && (inv.invoice_total || 0) > 250000) {
        b2clList.push({
          pos,
          inum: inv.invoice_number,
          idt: inv.invoice_date,
          val: inv.invoice_total,
          itms: (inv.line_items || []).map((l, idx) => ({
            num: idx + 1,
            itm_det: {
              txval: l.taxable_value,
              rt: (l.cgst_rate || 0) + (l.sgst_rate || 0) + (l.igst_rate || 0),
              iamt: l.igst_amount || 0,
              camt: 0,
              samt: 0,
              csamt: l.cess_amount || 0
            }
          }))
        });
      } else {
        // Table 7: B2C Small (b2cs) aggregated by sply_ty, pos, and tax rate
        const sply_ty = isInterState ? 'INTER' : 'INTRA';
        for (const l of inv.line_items || []) {
          const rate = (l.cgst_rate || 0) + (l.sgst_rate || 0) + (l.igst_rate || 0) || 18;
          const key = `${sply_ty}_${pos}_${rate}`;

          if (!b2csMap[key]) {
            b2csMap[key] = {
              sply_ty,
              pos,
              rt: rate,
              txval: new Decimal(0),
              iamt: new Decimal(0),
              camt: new Decimal(0),
              samt: new Decimal(0),
              csamt: new Decimal(0)
            };
          }
          b2csMap[key].txval = b2csMap[key].txval.plus(l.taxable_value || 0);
          b2csMap[key].iamt = b2csMap[key].iamt.plus(l.igst_amount || 0);
          b2csMap[key].camt = b2csMap[key].camt.plus(l.cgst_amount || 0);
          b2csMap[key].samt = b2csMap[key].samt.plus(l.sgst_amount || 0);
          b2csMap[key].csamt = b2csMap[key].csamt.plus(l.cess_amount || 0);
        }
      }
    }

    // Accumulate Table 12: HSN Summary
    for (const l of inv.line_items || []) {
      const code = (l.hsn_sac_code || "9983").trim();
      if (!hsnMap[code]) {
        hsnMap[code] = {
          desc: l.description || "General Services/Goods",
          qty: new Decimal(0),
          val: new Decimal(0),
          taxable: new Decimal(0),
          iamt: new Decimal(0),
          camt: new Decimal(0),
          samt: new Decimal(0),
          csamt: new Decimal(0)
        };
      }
      hsnMap[code].qty = hsnMap[code].qty.plus(l.qty || 1);
      hsnMap[code].val = hsnMap[code].val.plus(
        (l.taxable_value || 0) + (l.cgst_amount || 0) + (l.sgst_amount || 0) + (l.igst_amount || 0) + (l.cess_amount || 0)
      );
      hsnMap[code].taxable = hsnMap[code].taxable.plus(l.taxable_value || 0);
      hsnMap[code].iamt = hsnMap[code].iamt.plus(l.igst_amount || 0);
      hsnMap[code].camt = hsnMap[code].camt.plus(l.cgst_amount || 0);
      hsnMap[code].samt = hsnMap[code].samt.plus(l.sgst_amount || 0);
      hsnMap[code].csamt = hsnMap[code].csamt.plus(l.cess_amount || 0);
    }
  }

  // Format B2B Payload
  const gstnB2bPayload: Gstr1B2bGroup[] = Object.keys(b2bMap).map(ctin => ({
    ctin,
    inv: b2bMap[ctin]
  }));

  // Format B2CS Payload
  const gstnB2csPayload: Gstr1B2csItem[] = Object.values(b2csMap).map(item => ({
    sply_ty: item.sply_ty,
    pos: item.pos,
    typ: 'OE',
    rt: item.rt,
    txval: item.txval.toDecimalPlaces(2).toNumber(),
    iamt: item.iamt.toDecimalPlaces(2).toNumber(),
    camt: item.camt.toDecimalPlaces(2).toNumber(),
    samt: item.samt.toDecimalPlaces(2).toNumber(),
    csamt: item.csamt.toDecimalPlaces(2).toNumber()
  }));

  // Format HSN Summary
  const hsnItems: Gstr1HsnItem[] = Object.keys(hsnMap).map((hsn, i) => ({
    num: i + 1,
    hsn_sc: hsn,
    desc: hsnMap[hsn].desc,
    uqc: hsn.startsWith('99') ? 'OTH' : 'NOS',
    qty: hsnMap[hsn].qty.toNumber(),
    val: hsnMap[hsn].val.toDecimalPlaces(2).toNumber(),
    txval: hsnMap[hsn].taxable.toDecimalPlaces(2).toNumber(),
    iamt: hsnMap[hsn].iamt.toDecimalPlaces(2).toNumber(),
    camt: hsnMap[hsn].camt.toDecimalPlaces(2).toNumber(),
    samt: hsnMap[hsn].samt.toDecimalPlaces(2).toNumber(),
    csamt: hsnMap[hsn].csamt.toDecimalPlaces(2).toNumber()
  }));

  // Format Table 13: Document Issue Register
  const docIssuePayload: Gstr1DocIssueSection = {
    doc_det: [
      {
        doc_num: 1, // Invoices for outward supply
        docs: [
          {
            doc_num: 1,
            from: docNumbers.length > 0 ? docNumbers[0] : 'INV-001',
            to: docNumbers.length > 0 ? docNumbers[docNumbers.length - 1] : 'INV-001',
            totnum: docNumbers.length,
            cancel: 0,
            net_issue: docNumbers.length
          }
        ]
      }
    ]
  };

  const totalTaxable = b2bTaxable.plus(b2cTaxable).plus(exportTaxable).toDecimalPlaces(2).toNumber();
  const totalTax = b2bTax.plus(b2cTax).plus(exportTax).toDecimalPlaces(2).toNumber();

  const officialGstnJson: Gstr1OfficialPayload = {
    gstin,
    fp,
    gt: totalTaxable,
    cur_gt: totalTaxable,
    b2b: gstnB2bPayload.length > 0 ? gstnB2bPayload : undefined,
    b2cl: b2clList.length > 0 ? b2clList : undefined,
    b2cs: gstnB2csPayload.length > 0 ? gstnB2csPayload : undefined,
    exp: expInvoices.length > 0 ? [{ exp_typ: 'EXPWP', inv: expInvoices }] : undefined,
    hsn: hsnItems.length > 0 ? { data: hsnItems } : undefined,
    doc_issue: docIssuePayload
  };

  return {
    financialYear,
    taxPeriod,
    gstin,
    b2bCount,
    b2bTaxable: b2bTaxable.toDecimalPlaces(2).toNumber(),
    b2bTax: b2bTax.toDecimalPlaces(2).toNumber(),
    b2cCount,
    b2cTaxable: b2cTaxable.toDecimalPlaces(2).toNumber(),
    b2cTax: b2cTax.toDecimalPlaces(2).toNumber(),
    exportCount,
    exportTaxable: exportTaxable.toDecimalPlaces(2).toNumber(),
    exportTax: exportTax.toDecimalPlaces(2).toNumber(),
    hsnSummary: hsnItems.map(h => ({
      hsn: h.hsn_sc,
      description: h.desc,
      qty: h.qty,
      taxable: h.txval,
      tax: h.iamt + h.camt + h.samt + h.csamt
    })),
    totalTaxable,
    totalTax,
    gstnJsonPayload: officialGstnJson
  };
}

/**
 * Generates GSTR-3B Summary and Official GSTN JSON
 * Full Table 3.1, 3.2, 4, 5, and 6.1 Tax Payment Offset Engine
 * Replicated from India Compliance gstr_3b_report.py
 */
export function generateGstr3b(
  gstin: string,
  financialYear: string,
  taxPeriod: string,
  salesInvoices: ExtractedGstInvoice[],
  purchaseInvoices: ExtractedGstInvoice[],
  gstr2bRecords: Gstr2bRecord[]
): Gstr3bSummary {
  const fp = formatGstnReturnPeriod(taxPeriod, financialYear);
  const companyState = gstin.substring(0, 2);

  // 1. Table 3.1(a): Outward Taxable Supplies
  let outTaxable = new Decimal(0);
  let outIgst = new Decimal(0);
  let outCgst = new Decimal(0);
  let outSgst = new Decimal(0);
  let outCess = new Decimal(0);

  // Table 3.1(b): Zero-Rated (Exports)
  let zeroTaxable = new Decimal(0);
  let zeroIgst = new Decimal(0);
  let zeroCess = new Decimal(0);

  // Table 3.2: Inter-state supplies to unregistered persons
  const interStateUnregMap: Record<string, { txval: Decimal; iamt: Decimal }> = {};

  const sales = salesInvoices.filter(inv => inv.category === 'B2B_SALES' || inv.category === 'B2C_RETAIL');
  for (const inv of sales) {
    const isExport = (inv.place_of_supply || "").toLowerCase().includes("export") || inv.buyer_gstin === "EXPORT";
    const buyerGstin = (inv.buyer_gstin || "").trim().toUpperCase();
    const isUnregistered = !buyerGstin || buyerGstin.length !== 15 || buyerGstin === "URP";
    const pos = (inv.place_of_supply || companyState).substring(0, 2);
    const isInterState = pos !== companyState;

    if (isExport) {
      for (const l of inv.line_items || []) {
        zeroTaxable = zeroTaxable.plus(l.taxable_value || 0);
        zeroIgst = zeroIgst.plus(l.igst_amount || 0);
        zeroCess = zeroCess.plus(l.cess_amount || 0);
      }
    } else {
      for (const l of inv.line_items || []) {
        outTaxable = outTaxable.plus(l.taxable_value || 0);
        outIgst = outIgst.plus(l.igst_amount || 0);
        outCgst = outCgst.plus(l.cgst_amount || 0);
        outSgst = outSgst.plus(l.sgst_amount || 0);
        outCess = outCess.plus(l.cess_amount || 0);

        if (isInterState && isUnregistered) {
          if (!interStateUnregMap[pos]) {
            interStateUnregMap[pos] = { txval: new Decimal(0), iamt: new Decimal(0) };
          }
          interStateUnregMap[pos].txval = interStateUnregMap[pos].txval.plus(l.taxable_value || 0);
          interStateUnregMap[pos].iamt = interStateUnregMap[pos].iamt.plus(l.igst_amount || 0);
        }
      }
    }
  }

  // 2. Table 3.1(d): Inward Supplies liable to Reverse Charge (RCM)
  let rcmTaxable = new Decimal(0);
  let rcmIgst = new Decimal(0);
  let rcmCgst = new Decimal(0);
  let rcmSgst = new Decimal(0);
  let rcmCess = new Decimal(0);

  const rcmInvoices = purchaseInvoices.filter(
    inv => inv.reverse_charge_applicable || inv.itc_eligibility === 'RCM_APPLICABLE'
  );

  for (const inv of rcmInvoices) {
    for (const l of inv.line_items || []) {
      rcmTaxable = rcmTaxable.plus(l.taxable_value || 0);
      rcmIgst = rcmIgst.plus(l.igst_amount || 0);
      rcmCgst = rcmCgst.plus(l.cgst_amount || 0);
      rcmSgst = rcmSgst.plus(l.sgst_amount || 0);
      rcmCess = rcmCess.plus(l.cess_amount || 0);
    }
  }

  // 3. Table 4: Eligible ITC from 2B Recon minus Blocked Sec 17(5)
  const recon = runGstr2bReconciliation(purchaseInvoices, gstr2bRecords);

  // Eligible ITC: All matched in 2B that are ELIGIBLE
  let itcIgst = new Decimal(0);
  let itcCgst = new Decimal(0);
  let itcSgst = new Decimal(0);
  let itcCess = new Decimal(0);

  const matchedEligible = recon.results.filter(
    r => (r.reconStatus === 'EXACT_MATCH' || r.reconStatus === 'PARTIAL_MATCH') &&
         r.invoice.itc_eligibility === 'ELIGIBLE'
  );

  for (const item of matchedEligible) {
    for (const l of item.invoice.line_items || []) {
      itcIgst = itcIgst.plus(l.igst_amount || 0);
      itcCgst = itcCgst.plus(l.cgst_amount || 0);
      itcSgst = itcSgst.plus(l.sgst_amount || 0);
      itcCess = itcCess.plus(l.cess_amount || 0);
    }
  }

  // Blocked credits under Section 17(5)
  const blockedSec17_5 = recon.results.filter(
    r => r.invoice.itc_eligibility === 'BLOCKED_17_5'
  );
  let blockedItcValue = new Decimal(0);
  for (const item of blockedSec17_5) {
    blockedItcValue = blockedItcValue.plus(item.invoice.total_tax || 0);
  }

  // Add RCM to Eligible ITC (Table 4.A.3)
  const totalEligibleItc = itcIgst.plus(itcCgst).plus(itcSgst).plus(itcCess)
    .plus(rcmIgst).plus(rcmCgst).plus(rcmSgst).plus(rcmCess);
  const netItcAvailable = totalEligibleItc;

  // 4. Table 6.1 Tax Liability Cross-Utilization Offset Engine (CGST Act Section 49(5))
  // Total Liability = Outward Taxable + Outward Zero-Rated + Inward RCM
  const liabIgst = outIgst.plus(zeroIgst).plus(rcmIgst);
  const liabCgst = outCgst.plus(rcmCgst);
  const liabSgst = outSgst.plus(rcmSgst);
  const liabCess = outCess.plus(rcmCess);

  // Total Available Credit (including RCM ITC)
  let availIgst = itcIgst.plus(rcmIgst);
  let availCgst = itcCgst.plus(rcmCgst);
  let availSgst = itcSgst.plus(rcmSgst);
  let availCess = itcCess.plus(rcmCess);

  // Step 1: Pay IGST liability using IGST credit
  const paidIgstFromIgst = Decimal.min(liabIgst, availIgst);
  availIgst = availIgst.minus(paidIgstFromIgst);
  const remainLiabIgst = liabIgst.minus(paidIgstFromIgst);

  // Step 2: Pay CGST liability using CGST credit, then remaining IGST credit
  const paidCgstFromCgst = Decimal.min(liabCgst, availCgst);
  availCgst = availCgst.minus(paidCgstFromCgst);
  let remainLiabCgst = liabCgst.minus(paidCgstFromCgst);

  const paidCgstFromIgst = Decimal.min(remainLiabCgst, availIgst);
  availIgst = availIgst.minus(paidCgstFromIgst);
  remainLiabCgst = remainLiabCgst.minus(paidCgstFromIgst);

  // Step 3: Pay SGST liability using SGST credit, then remaining IGST credit
  const paidSgstFromSgst = Decimal.min(liabSgst, availSgst);
  availSgst = availSgst.minus(paidSgstFromSgst);
  let remainLiabSgst = liabSgst.minus(paidSgstFromSgst);

  const paidSgstFromIgst = Decimal.min(remainLiabSgst, availIgst);
  availIgst = availIgst.minus(paidSgstFromIgst);
  remainLiabSgst = remainLiabSgst.minus(paidSgstFromIgst);

  // Step 4: Pay Cess liability
  const paidCessFromCess = Decimal.min(liabCess, availCess);
  availCess = availCess.minus(paidCessFromCess);
  const remainLiabCess = liabCess.minus(paidCessFromCess);

  // Remaining unpaid liability must be paid in cash
  const netPayableIgst = remainLiabIgst;
  const netPayableCgst = remainLiabCgst;
  const netPayableSgst = remainLiabSgst;
  const netPayableCess = remainLiabCess;

  // Format Official Government JSON matching India Compliance GSTR-3B schema
  const officialGstnPayload: Gstr3bOfficialPayload = {
    gstin,
    ret_period: fp,
    sup_details: {
      osup_det: {
        txval: outTaxable.toDecimalPlaces(2).toNumber(),
        iamt: outIgst.toDecimalPlaces(2).toNumber(),
        camt: outCgst.toDecimalPlaces(2).toNumber(),
        samt: outSgst.toDecimalPlaces(2).toNumber(),
        csamt: outCess.toDecimalPlaces(2).toNumber()
      },
      osup_zero: {
        txval: zeroTaxable.toDecimalPlaces(2).toNumber(),
        iamt: zeroIgst.toDecimalPlaces(2).toNumber(),
        csamt: zeroCess.toDecimalPlaces(2).toNumber()
      },
      osup_nil_exmp: { txval: 0 },
      isup_rev: {
        txval: rcmTaxable.toDecimalPlaces(2).toNumber(),
        iamt: rcmIgst.toDecimalPlaces(2).toNumber(),
        camt: rcmCgst.toDecimalPlaces(2).toNumber(),
        samt: rcmSgst.toDecimalPlaces(2).toNumber(),
        csamt: rcmCess.toDecimalPlaces(2).toNumber()
      },
      osup_nongst: { txval: 0 }
    },
    inter_sup: {
      unreg_details: Object.keys(interStateUnregMap).map(pos => ({
        pos,
        txval: interStateUnregMap[pos].txval.toDecimalPlaces(2).toNumber(),
        iamt: interStateUnregMap[pos].iamt.toDecimalPlaces(2).toNumber()
      })),
      comp_details: [],
      uin_details: []
    },
    itc_elg: {
      itc_avl: {
        IMPG: { iamt: 0, csamt: 0 },
        IMPS: { iamt: 0, csamt: 0 },
        ISRC: {
          iamt: rcmIgst.toDecimalPlaces(2).toNumber(),
          camt: rcmCgst.toDecimalPlaces(2).toNumber(),
          samt: rcmSgst.toDecimalPlaces(2).toNumber(),
          csamt: rcmCess.toDecimalPlaces(2).toNumber()
        },
        ISD: { iamt: 0, camt: 0, samt: 0, csamt: 0 },
        OTH: {
          iamt: itcIgst.toDecimalPlaces(2).toNumber(),
          camt: itcCgst.toDecimalPlaces(2).toNumber(),
          samt: itcSgst.toDecimalPlaces(2).toNumber(),
          csamt: itcCess.toDecimalPlaces(2).toNumber()
        }
      },
      itc_rev: {
        RUL: {
          iamt: 0,
          camt: blockedItcValue.dividedBy(2).toDecimalPlaces(2).toNumber(),
          samt: blockedItcValue.dividedBy(2).toDecimalPlaces(2).toNumber(),
          csamt: 0
        },
        OTH: { iamt: 0, camt: 0, samt: 0, csamt: 0 }
      },
      itc_net: {
        iamt: itcIgst.plus(rcmIgst).toDecimalPlaces(2).toNumber(),
        camt: itcCgst.plus(rcmCgst).minus(blockedItcValue.dividedBy(2)).toDecimalPlaces(2).toNumber(),
        samt: itcSgst.plus(rcmSgst).minus(blockedItcValue.dividedBy(2)).toDecimalPlaces(2).toNumber(),
        csamt: itcCess.plus(rcmCess).toDecimalPlaces(2).toNumber()
      },
      itc_inelg: {
        RUL: {
          iamt: 0,
          camt: blockedItcValue.dividedBy(2).toDecimalPlaces(2).toNumber(),
          samt: blockedItcValue.dividedBy(2).toDecimalPlaces(2).toNumber(),
          csamt: 0
        },
        OTH: { iamt: 0, camt: 0, samt: 0, csamt: 0 }
      }
    },
    inward_sup: {
      isup_details: [
        { ty: 'GST', inter: 0, intra: 0 },
        { ty: 'NONGST', inter: 0, intra: 0 }
      ]
    }
  };

  return {
    financialYear,
    taxPeriod,
    gstin,
    table3_1_outward_taxable: {
      taxable_value: outTaxable.toDecimalPlaces(2).toNumber(),
      igst: outIgst.toDecimalPlaces(2).toNumber(),
      cgst: outCgst.toDecimalPlaces(2).toNumber(),
      sgst: outSgst.toDecimalPlaces(2).toNumber(),
      cess: outCess.toDecimalPlaces(2).toNumber()
    },
    table3_1_inward_rcm: {
      taxable_value: rcmTaxable.toDecimalPlaces(2).toNumber(),
      igst: rcmIgst.toDecimalPlaces(2).toNumber(),
      cgst: rcmCgst.toDecimalPlaces(2).toNumber(),
      sgst: rcmSgst.toDecimalPlaces(2).toNumber(),
      cess: rcmCess.toDecimalPlaces(2).toNumber()
    },
    table4_eligible_itc: {
      import_of_services: 0,
      inward_rcm: rcmCgst.plus(rcmSgst).plus(rcmIgst).toDecimalPlaces(2).toNumber(),
      all_other_itc_2b: itcIgst.plus(itcCgst).plus(itcSgst).plus(itcCess).toDecimalPlaces(2).toNumber(),
      total_eligible_itc: totalEligibleItc.toDecimalPlaces(2).toNumber()
    },
    table4_ineligible_itc_17_5: blockedItcValue.toDecimalPlaces(2).toNumber(),
    net_itc_available: netItcAvailable.toDecimalPlaces(2).toNumber(),
    net_tax_payable: {
      igst: Math.max(0, netPayableIgst.toDecimalPlaces(2).toNumber()),
      cgst: Math.max(0, netPayableCgst.toDecimalPlaces(2).toNumber()),
      sgst: Math.max(0, netPayableSgst.toDecimalPlaces(2).toNumber()),
      cess: Math.max(0, netPayableCess.toDecimalPlaces(2).toNumber())
    },
    gstnJsonPayload: officialGstnPayload
  };
}

/**
 * 1-Click Annual Consolidator (GSTR-9 Engine)
 * Aggregates all 12 months (April through March) for the selected financial year
 */
export function generateGstr9AnnualSummary(
  gstin: string,
  financialYear: string,
  allInvoicesInFy: ExtractedGstInvoice[],
  all2bRecordsInFy: Gstr2bRecord[]
): Gstr9AnnualSummary {
  const months = [
    "April", "May", "June", "July", "August", "September",
    "October", "November", "December", "January", "February", "March"
  ];

  let totalSalesTaxable = new Decimal(0);
  let totalOutputTax = new Decimal(0);
  let totalItcClaimed3B = new Decimal(0);
  let totalItcAvailable2B = new Decimal(0);
  let totalRcmTax = new Decimal(0);

  const monthlyBreakdown = months.map(m => {
    const monthSales = allInvoicesInFy.filter(
      i => i.taxPeriod === m && (i.category === 'B2B_SALES' || i.category === 'B2C_RETAIL')
    );
    const monthPurchases = allInvoicesInFy.filter(
      i => i.taxPeriod === m && (i.category === 'B2B_PURCHASES' || i.category === 'EXPENSE_RECEIPT')
    );
    const month2b = all2bRecordsInFy.filter(r => r.taxPeriod === m);

    let sTaxable = new Decimal(0);
    let sTax = new Decimal(0);
    for (const inv of monthSales) {
      sTaxable = sTaxable.plus(inv.total_taxable_value || 0);
      sTax = sTax.plus(inv.total_tax || 0);
    }

    let pTax2b = new Decimal(0);
    for (const r of month2b) {
      pTax2b = pTax2b.plus(r.integrated_tax || 0)
        .plus(r.central_tax || 0)
        .plus(r.state_tax || 0)
        .plus(r.cess_amount || 0);
    }

    // Reconcile purchases for this month
    const recon = runGstr2bReconciliation(monthPurchases, month2b);
    const claimedItc = new Decimal(recon.summary.matchedItc);

    // RCM
    for (const inv of monthPurchases) {
      if (inv.reverse_charge_applicable || inv.itc_eligibility === 'RCM_APPLICABLE') {
        totalRcmTax = totalRcmTax.plus(inv.total_tax || 0);
      }
    }

    totalSalesTaxable = totalSalesTaxable.plus(sTaxable);
    totalOutputTax = totalOutputTax.plus(sTax);
    totalItcClaimed3B = totalItcClaimed3B.plus(claimedItc);
    totalItcAvailable2B = totalItcAvailable2B.plus(pTax2b);

    const variance = claimedItc.minus(pTax2b).toNumber();

    return {
      month: m,
      salesTaxable: sTaxable.toDecimalPlaces(2).toNumber(),
      outputTax: sTax.toDecimalPlaces(2).toNumber(),
      itcClaimed: claimedItc.toDecimalPlaces(2).toNumber(),
      itcIn2B: pTax2b.toDecimalPlaces(2).toNumber(),
      variance: Number(variance.toFixed(2))
    };
  });

  const itcGapAtRisk = totalItcClaimed3B.minus(totalItcAvailable2B).toNumber();

  return {
    financialYear,
    gstin,
    totalSalesTaxable: totalSalesTaxable.toDecimalPlaces(2).toNumber(),
    totalOutputTax: totalOutputTax.toDecimalPlaces(2).toNumber(),
    totalItcClaimed3B: totalItcClaimed3B.toDecimalPlaces(2).toNumber(),
    totalItcAvailable2B: totalItcAvailable2B.toDecimalPlaces(2).toNumber(),
    itcGapAtRisk: Number(Math.max(0, itcGapAtRisk).toFixed(2)),
    totalRcmTax: totalRcmTax.toDecimalPlaces(2).toNumber(),
    monthlyBreakdown,
    status: totalSalesTaxable.gt(0) ? 'READY_FOR_AUDIT' : 'DRAFT'
  };
}
